import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

interface StarRatingInputProps {
  /**
   * Current rating value (1-5)
   */
  value: number;
  /**
   * Callback when rating changes
   */
  onChange: (rating: number) => void;
  /**
   * Size of stars in pixels
   * @default 32
   */
  size?: number;
  /**
   * Color of filled stars
   * @default '#FFA500' (orange)
   */
  color?: string;
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the input is in an error state
   * @default false
   */
  error?: boolean;
  /**
   * Helper text to display below stars
   */
  helperText?: string;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent'
};

/**
 * StarRatingInput - Interactive star rating input component
 *
 * Allows users to select a rating by clicking on stars.
 * Supports hover preview and displays rating label.
 * For display-only ratings, use StarRating component.
 *
 * @example
 * <StarRatingInput
 *   value={rating}
 *   onChange={setRating}
 *   error={!!errors.rating}
 *   helperText={errors.rating?.message}
 * />
 */
export function StarRatingInput({
  value,
  onChange,
  size = 32,
  color = '#FFA500',
  disabled = false,
  error = false,
  helperText
}: StarRatingInputProps) {
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const displayRating = hoverRating ?? value;
  const ratingLabel = displayRating > 0 ? RATING_LABELS[displayRating] : 'Select rating';

  const handleClick = (rating: number) => {
    if (!disabled) {
      onChange(rating);
    }
  };

  const handleMouseEnter = (rating: number) => {
    if (!disabled) {
      setHoverRating(rating);
    }
  };

  const handleMouseLeave = () => {
    setHoverRating(null);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          opacity: disabled ? 0.5 : 1
        }}
        onMouseLeave={handleMouseLeave}
      >
        {[1, 2, 3, 4, 5].map((rating) => {
          const isFilled = rating <= displayRating;
          const Icon = isFilled ? StarIcon : StarOutlineIcon;

          return (
            <Box
              key={rating}
              onClick={() => handleClick(rating)}
              onMouseEnter={() => handleMouseEnter(rating)}
              sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'transform 0.2s',
                '&:hover': !disabled ? {
                  transform: 'scale(1.15)'
                } : {}
              }}
            >
              <Icon
                sx={{
                  fontSize: size,
                  color: isFilled ? color : '#E0E0E0',
                  transition: 'color 0.2s'
                }}
              />
            </Box>
          );
        })}
        <Typography
          variant="body2"
          sx={{
            ml: 1,
            fontWeight: 600,
            color: error ? 'error.main' : 'text.secondary',
            minWidth: 80
          }}
        >
          {ratingLabel}
        </Typography>
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: error ? 'error.main' : 'text.secondary'
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
